import * as Sentry from "@sentry/browser";
import packageJson from "../../package.json";
import { isExpectedRequestCancellation } from "./requestErrors";

const dsn = process.env.REACT_APP_SENTRY_DSN;
let enabled = Boolean(dsn);
const release =
  process.env.REACT_APP_SENTRY_RELEASE ||
  `${packageJson.name}@${packageJson.version}`;

const numberFromEnv = (name, fallback) => {
  const value = process.env[name];
  if (value === undefined || value === "") return fallback;
  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : fallback;
};

if (enabled) {
  try {
    Sentry.init({
      dsn,
      environment: process.env.REACT_APP_SENTRY_ENVIRONMENT || process.env.NODE_ENV,
      release,
      autoSessionTracking: true,
      tracesSampleRate: numberFromEnv("REACT_APP_SENTRY_TRACES_SAMPLE_RATE", 0.1),
      integrations: [
        Sentry.browserTracingIntegration(),
      ],
      beforeSend(event, hint) {
        return isExpectedRequestCancellation(hint?.originalException) ? null : event;
      },
    });
    Sentry.setTag("service", "frontend");
    Sentry.setTag("release", release);
  } catch (error) {
    enabled = false;
    // Sentry should never prevent the app from rendering.
    console.warn("Sentry initialization failed", error);
  }
}

export const captureError = (error, context = {}) => {
  if (!enabled || isExpectedRequestCancellation(error)) return;
  Sentry.withScope((scope) => {
    Object.entries(context).forEach(([key, value]) => {
      scope.setExtra(key, value);
    });
    if (!(error instanceof Error)) {
      scope.setExtra("nonError", error);
      Sentry.captureMessage(String(error?.message || error || "Non-Error exception"), "error");
      return;
    }
    Sentry.captureException(error);
  });
};

export const captureMessage = (message, level = "info", context = {}) => {
  if (!enabled) return;
  Sentry.withScope((scope) => {
    Object.entries(context).forEach(([key, value]) => {
      scope.setExtra(key, value);
    });
    Sentry.captureMessage(message, level);
  });
};

export const addBreadcrumb = (message, category = "app", data = {}, level = "info") => {
  if (!enabled) return;
  Sentry.addBreadcrumb({
    message,
    category,
    level,
    data,
  });
};

export const setSentryUser = (user) => {
  if (!enabled) return;
  if (!user) {
    Sentry.setUser(null);
    return;
  }
  Sentry.setUser({
    id: user.id,
    email: user.email,
    username: user.name,
  });
};
